import {
  IPracticeTypeSuggestionEvaluateRequest,
  IPracticeTypeSuggestionEvaluateResult,
  IPracticeTypeSuggestionRule,
  IPracticeTypeSuggestionRuleSaveRequest,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { api } from ".";
import { PRACTICE_TYPE_EVALUATE_TAG } from "./practiceTypeTags";

/**
 * CLE-2971 — Service Type rules.
 *
 * EVALUATE: would the account's rules switch this check's Service Type? CreateOrder asks when CPT
 * codes are added after eligibility ran (`usePracticeTypeAutoRerun`) and re-runs the check if so; the
 * API does the switch itself. A POST that only reads, so it is a QUERY rather than a mutation: RTK
 * Query caches it by its arguments, and the caller reads `currentData`, which belongs to the arguments
 * it asked with. Any user with access to the account may call it. The Service Type Rules tester calls
 * it through the LAZY hook, whose trigger always refetches, so it runs against the rules as saved.
 *
 * LIST / CREATE / UPDATE / DELETE: Eligibility › Service Type Rules. SuperAdmin only. The tag is
 * registered here rather than in the shared tagTypes list, the same as Payer Auto-Selection; every
 * write invalidates the list.
 */
const apiWithPracticeTypeSuggestionTags = api.enhanceEndpoints({
  addTagTypes: ["PracticeTypeSuggestionRules"],
});

export const PRACTICE_TYPE_SUGGESTION_PATH =
  "eligibility/practice-type-suggestion-rules";

const LIST_TAG = { type: "PracticeTypeSuggestionRules" as const, id: "LIST" };

export const practiceTypeSuggestionQueries =
  apiWithPracticeTypeSuggestionTags.injectEndpoints({
    endpoints: (builder) => ({
      evaluatePracticeTypeSuggestion: builder.query<
        IPracticeTypeSuggestionEvaluateResult,
        IPracticeTypeSuggestionEvaluateRequest
      >({
        query: (body) => ({
          url: `${PRACTICE_TYPE_SUGGESTION_PATH}/evaluate`,
          method: "POST",
          body,
        }),
        transformResponse: (response: {
          data: IPracticeTypeSuggestionEvaluateResult;
        }) => response.data,
        providesTags: [PRACTICE_TYPE_EVALUATE_TAG],
      }),
      /** With an account: that hospital's rules AND the all-hospitals rules. Without: every rule. */
      getPracticeTypeSuggestionRules: builder.query<
        IPracticeTypeSuggestionRule[],
        string | null
      >({
        query: (accountId) =>
          accountId
            ? `${PRACTICE_TYPE_SUGGESTION_PATH}?accountId=${encodeURIComponent(
                accountId
              )}`
            : PRACTICE_TYPE_SUGGESTION_PATH,
        transformResponse: (response: {
          data: IPracticeTypeSuggestionRule[];
        }) => response.data,
        providesTags: [LIST_TAG],
      }),
      createPracticeTypeSuggestionRule: builder.mutation<
        IPracticeTypeSuggestionRule,
        IPracticeTypeSuggestionRuleSaveRequest
      >({
        query: (body) => ({
          url: PRACTICE_TYPE_SUGGESTION_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IPracticeTypeSuggestionRule }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      updatePracticeTypeSuggestionRule: builder.mutation<
        IPracticeTypeSuggestionRule,
        { id: string; body: IPracticeTypeSuggestionRuleSaveRequest }
      >({
        query: ({ id, body }) => ({
          url: `${PRACTICE_TYPE_SUGGESTION_PATH}/${encodeURIComponent(id)}`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IPracticeTypeSuggestionRule }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      deletePracticeTypeSuggestionRule: builder.mutation<
        { id: string },
        string
      >({
        query: (id) => ({
          url: `${PRACTICE_TYPE_SUGGESTION_PATH}/${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        transformResponse: (response: { data: { id: string } }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
    }),
  });

export const {
  useEvaluatePracticeTypeSuggestionQuery,
  useLazyEvaluatePracticeTypeSuggestionQuery,
  useGetPracticeTypeSuggestionRulesQuery,
  useCreatePracticeTypeSuggestionRuleMutation,
  useUpdatePracticeTypeSuggestionRuleMutation,
  useDeletePracticeTypeSuggestionRuleMutation,
} = practiceTypeSuggestionQueries;
